import { Button, Dialog, IconButton } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Fragment, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IoIosAddCircle } from "react-icons/io";
import { useParams } from "react-router-dom";

import dayjs from "dayjs";
import { EDIT_INPATIENT_STAY } from "../../../../enums/ApiPaths";
import { GET_INPATIENT_STAY } from "../../../../enums/QueryKeys";
import {
  capitalizeFirstLetter,
  useIsMobile,
} from "../../../../utils/Functions";
import useSubmit from "../../../master/species/hooks/useSubmit";
import AddInpatientStayStepper from "./AddInpatientStayStepper";

export default function AddInpatientStay({ selectedPetId, inpatientStay }) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const { id: boxId } = useParams();
  const queryClient = useQueryClient();
  const submittion = useSubmit(null, EDIT_INPATIENT_STAY);

  const methods = useForm({
    defaultValues: {
      vaccines: [],
    },
  });
  
  const parseDate = (dateStr) =>
    dateStr ? dayjs(dateStr, "DD/MM/YYYY") : null;

  useEffect(() => {
    if (open && inpatientStay) {
      const data = inpatientStay;
      if (data) {
        methods.reset({
          petId: selectedPetId,
          dateOfEntrance: parseDate(data.dateOfEntrance),
          dateOfExit: parseDate(data.dateOfExit),
          corporal: data.corporal,
          fivFelv: data.fivFelv,
          ampoule: data.ampoule,
          nextVaccineDate: parseDate(data.nextVaccineDate),
          antiParasite: data.antiParasite,
          dateOfAntiParasite: parseDate(data.dateOfAntiParasite),
          nextAntiParasiteDate: parseDate(data.nextAntiParasiteDate),
          vaccines:
            data.vaccines?.map((v) => ({
              ...v,
              date: parseDate(v.date),
              category: capitalizeFirstLetter(v.category),
            })) || [],
        });
      }
    }
  }, [open, inpatientStay, methods]);

  const handleOpenDialog = () => setOpen(true);

  const handleCloseDialog = () => {
    methods.reset();
    setOpen(false);
  };
  const formatDate = (date) =>
    date && date.format ? date.format("DD/MM/YYYY") : null;

  const submitData = async (values) => {
    const submitObj = {
      boxId: boxId,
      inpatientStayId: selectedPetId,
      petId: selectedPetId ? selectedPetId : null,
      dateOfEntrance: formatDate(values.dateOfEntrance),
      dateOfExit: formatDate(values.dateOfExit),
      ampoule: values.ampoule,
      fivFelv: values.fivFelv,
      corporal: !!values.corporal,
      nextVaccineDate: formatDate(values.nextVaccineDate),
      antiParasite: values.antiParasite,
      dateOfAntiParasite: formatDate(values.dateOfAntiParasite),
      nextAntiParasiteDate: formatDate(values.nextAntiParasiteDate),

      vaccines: values.vaccines?.map((v) => ({
        name: v.name,
        date: formatDate(v.date),
        category: v.category?.toUpperCase(),
      })),
    };

    const res = await submittion.submitCallback(submitObj);
    if (res) {
      await queryClient.invalidateQueries({
        queryKey: [`${GET_INPATIENT_STAY}${selectedPetId}`],
      });
      handleCloseDialog();
    }
  };

  return (
    <Fragment>
      {isMobile ? (
        <IconButton onClick={handleOpenDialog} color="primary" title={t("EditInpatientStay")}>
          <IoIosAddCircle />
        </IconButton>
      ) : (
        <Button variant="contained" onClick={handleOpenDialog}>
          {t("EditInpatientStay")}
        </Button>
      )}

      <Dialog
        open={open}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <FormProvider {...methods}>
          <form
            id="inpatient-stay-form"
            onSubmit={methods.handleSubmit(submitData)}
          >
            <AddInpatientStayStepper handleCloseDialog={handleCloseDialog} selectedPetId={selectedPetId}/>
          </form>
        </FormProvider>
      </Dialog>
    </Fragment>
  );
}
